<template>
  <div>
    <list-header title="Coupons" class="mb-4" :style="{position: 'relative'}">
      <icon-button
        :icon-class="'bi bi-three-dots'"
        @click="showGroupByModal"
        :btnStyle="{
          bottom: '12px',
          right: '17px',
          position: 'absolute',
          fontSize: '18px',
        }"
      />
    </list-header>

    <pull-to-refresh :refreshFunction="getCoupons">
      <!-- Coupon Found -->
      <div v-if="populatedCoupons && populatedCoupons.length > 0"> <!-- if populatedCoupons is not undefined(init value) and is not empty array(API return data) -->
        <div class="style-content">

          <nav-tab
            :tabs="tabList"
            :selected-tab="selectedTabID"
            @selected-tab="selectedTabID = $event"
            cssClass="scss-tabs"
          >
            <div v-for="coupon in populatedCoupons" :key="`sec-${groupBy === 'section' ? coupon.sectionID : coupon.categoryID}`">
              <tab :isSelected="selectedTabID === (groupBy === 'section' ? coupon.sectionID : coupon.categoryID)">
                <fieldset id="group1">
                  <div
                    v-for="data in coupon.coupons"
                    :key="`srv-${data.id}`"
                    @click="viewItemDetail(data)"
                  >
                    <div
                      class="scss-round-container d-flex flex-row mb-2 scss-click-effect"
                    >
                      <expandable-list
                        class="flex-grow-1"
                        :border="data.promotion">
                        <expandable-list-header
                          v-if="data.promotion"
                          :content="getPromotionHeaderAlt(data)"/>
                        <expandable-list-item
                          :imgUrl="data.imageURL"
                          :name="data.name"
                          :processingTime="formatProcessingTime(data.type, data.processingTime)"
                          :description="data.description"
                          :originalPrice="data.originalPrice"
                          :salePrice="data.salePrice"
                          :clickable="false">
                          <div class="d-flex flex-row align-items-center me-2">
                            <icon-container
                              iconClass="bi-cart-fill"
                              :cssClass="'style-border'"
                              @click.stop="addToCart(data.id)"/>
                          </div>
                        </expandable-list-item>
                      </expandable-list>
                    </div>
                  </div>
                </fieldset>
              </tab>
            </div>
            <div class="style-action-container fixed-bottom">
              <badge-button
                :quantity="cartsCount"
                iconClass="bi-cart-fill"
                redirectURL="/my-cart"/>
            </div>
          </nav-tab>
        </div>
      </div>

      <!-- To avoid display loading spinner and showing `No more coupon found.` message -->
      <div v-else-if="loading">
        <item-not-found message="Loading coupon." />
      </div>

      <!-- Services not found -->
      <div v-else>
        <item-not-found message="No more coupon found." />
      </div>
    </pull-to-refresh>
    <product-listing-filter-modal
      :title="'Coupons Options'"
      :groupBy="groupBy"
      @update:groupBy="updateGroupBySelection"/>
  </div>
</template>

<script>
import {
  defineAsyncComponent,
  markRaw
} from "vue";
import store from "@/store";
import { mapActions, mapGetters, mapMutations } from "vuex";
import { getTodaysDate } from "@/common/helpers/DateTimeHelper.js";
import { getFormattedNumber } from "@/common/helpers/PriceHelper.js";
import {
  formatProcessingTime,
  getPromotionHeaderAlt
} from "@/common/helpers/ProductHelper";
import { getToken } from "@/services/jwtService";
import BadgeButton from "@/components/button/BadgeButton.vue";
import IconContainer from "@/components/IconContainer.vue";
import ListHeader from "@/components/ListHeader.vue";
import ExpandableList from "@/components/ExpandableList.vue";
import ExpandableListHeader from "@/components/ExpandableListHeader.vue";
import ExpandableListItem from "@/components/ExpandableListItem.vue";
import NavTab from "@/components/tab/NavTab.vue";
import IconButton from "@/components/button/IconButton.vue";
import ProductListingFilterModal, { Modal } from "../components/ProductListingFilterModal.vue";
import Tab from "@/components/tab/Tab.vue";
import ItemNotFound from "@/components/ItemNotFound.vue";
import PullToRefresh from "@/components/PullToRefresh";
import { openModal, closeModal } from "@/store/modal";

export default {
  name: "Coupons",
  components: {
    BadgeButton,
    IconContainer,
    ListHeader,
    ExpandableList,
    ExpandableListHeader,
    ExpandableListItem,
    NavTab,
    IconButton,
    ProductListingFilterModal,
    Tab,
    ItemNotFound,
    PullToRefresh,
  },
  data() {
    return {
      groupBy: "section", // value is either section/category. default as section.
      selectedTabID: null,
      selectedCouponID: null,
    };
  },
  computed: {
    isLoggedIn() {
      return getToken() != null;
    },
    populatedCoupons() {
      // defined listing - list of coupon section data
      const list = this.coupons?.reduce((accumulated, coupon) => {
        const sectionId = coupon?.productSection?.id || 0;
        const categoryId = coupon?.productCategory?.id || 0;
        const exists = this.groupBy === "section" ?
            accumulated.find((data) => data.sectionID == sectionId) : // group by section
            accumulated.find((data) => data.categoryID == categoryId); // group by category

        if (exists === undefined) {
          accumulated.push({
            sectionID: sectionId || 0,
            categoryID: categoryId || 0,
            sectionName: coupon?.productSection?.name || 'Others',
            categoryName: coupon?.productCategory?.name || 'Others',
            coupons: [],
          });
        };

        return accumulated;
      }, []);

      // populate coupon section data coupons list
      if (list !== undefined && list.length > 0) {
        list.map((data) => {
          data.coupons = this.coupons?.
            filter((coupon) => {
              const sectionId = coupon?.productSection?.id || 0;
              const categoryId = coupon?.productCategory?.id || 0;

              return this.groupBy === "section" ?
                sectionId === data.sectionID : // group by section
                categoryId === data.categoryID; // group by category
            })
            .map((coupon) => {
              return {
                id: coupon.id,
                name: coupon.name,
                type: coupon.type,
                description: coupon.description,
                processingTime: 0,
                formatProcessingTime: coupon.coupon.formatProcessingTime,
                imageURL: coupon.image,
                section: coupon.productSection?.name,
                category: coupon.productCategory?.name,
                promotion: coupon.promotion,
                salePrice: coupon.salePrice,
                originalPrice: coupon.originalPrice,
                promotionPrice: coupon.coupon.promotionPrice,
                promotionStartDate: coupon.coupon.startDate,
                promotionEndDate: coupon.coupon.endDate,
              };
            });
        });
      }

      return list;
    },
    tabList() {
      if (this.populatedCoupons) {
        return this.populatedCoupons
          .map((coupon) => {
            return this.groupBy === "section" ?
              // group by section
              {
                id: coupon.sectionID,
                name: `${coupon.sectionName} (${coupon.coupons.length})`,
              } :
              // group by category
              {
                id: coupon.categoryID,
                name: `${coupon.categoryName} (${coupon.coupons.length})`,
              };
          })
          .sort((a, b) => {
            // Make the others always at the end
            if (a.id === 0) {
              return 1;
            }
            if (b.id === 0) {
              return -1;
            }

            // Sort as normal
            return a.id > b.id ? 1 : -1;
          });
      }

      return [];
    },
    selectedCouponDetails() {
      return this.setSelectedCoupon(this.selectedCouponID);
    },
    buttonEnabled() {
      return Boolean(this.selectedCouponID);
    },
    ...mapGetters({
      coupons: "coupons",
      loading: "showLoading",
      carts: "carts",
      cartsCount: "cartsCount",
    }),
  },
  methods: {
    showGroupByModal() {
      var myModal = new Modal(document.getElementById("product-listing-filter-modal-id")); // use <product-listing-filter-modal /> default id
      myModal.show();
    },
    updateGroupBySelection(value) {
      this.groupBy = value;
      this.selectedTabID = null; // reset
    },
    async fetchData() {
      const actions = [];

      // If coupons is not fetched
      if (this.coupons === null) {
        actions.push("getCoupons");
      }

      // If user is logging and carts is empty
      if (this.isLoggedIn) {
        actions.push("getCarts");
      }

      try {
        actions.forEach((action) => store.dispatch(action));
      } catch (error) {
        console.log(error);
      }
    },
    setSelectedCoupons(id) {
      let couponList = this.populatedCoupons
        .map((coupon) => coupon.coupons)
        .flat(1);

      return couponList.find((coupon) => coupon.id === parseInt(id));
    },
    viewItemDetail(data) {
      openModal(
        {
          id: "expandableListItemDetailModal",
        },
        markRaw(defineAsyncComponent(() => import("@/components/ExpandableListItemDetail.vue"))),
        {
          image: data.imageURL,
          processingTime: data.formatProcessingTime,
          name: data.name,
          description: data.description,
          originalPrice: data.originalPrice,
          salePrice: data.salePrice,
          section: data.section,
          category: data.category,
          promotion: data.promotion,
        },
        {},
        [
          {
            text: 'Add to cart',
            class: 'scss-blue-button',
            clickFn: async () => {
              if (!this.isLoggedIn) {
                this.$router.push("/login");
                closeModal();
                return;
              }

              store.commit("showLoading", true);
              await store.dispatch('postCart', {
                productId: data.id
              });
              closeModal();
              store.commit("showLoading", false);
            }
          },
          {
            text: 'Close',
            clickFn: () => {
              closeModal();
            }
          }
        ]
      );
    },
    getTodaysDate,
    formatProcessingTime,
    getPromotionHeaderAlt,
    getFormattedNumber,
    async addToCart(productId) {
      if (!this.isLoggedIn) {
        this.$router.push("/login");
      }

      store.commit("showLoading", true);
      await store.dispatch('postCart', {
        productId
      });
      store.commit("showLoading", false);
    },
    ...mapActions([
      "getCarts",
      "getCoupons",
    ]),
    ...mapMutations(["showLoading"]),
  },
  watch: {
    populatedCoupons: {
      handler(newValue) {
        if (newValue !== undefined) {
          this.showLoading(false);
        } else {
          this.showLoading(true);
        }
      },
      immediate: true
    },
  },
  created() {
    this.fetchData();
  },
};
</script>

<style lang="scss" scoped>
.style-checkbox {
  font-size: 20px;
}
.style-action-container {
  margin: 0 auto 0 auto;
  padding-top: 5px;
  max-width: 510px;
  border: 1px solid $border-light-grey;
  background-color: white;
  z-index: 5;
}
.style-content {
  width: 90%;
  margin: 0 auto 0 auto;
  margin-bottom: 70px;
}
.style-action-container {
  background-color: white;
  border: 1px solid $border-light-grey;
  bottom: 0;
  margin: 0 auto 0 auto;
  max-width: 510px;
  padding-top: 5px;
  z-index: 5;
}
</style>
